import { ActivatedRoute, Router } from '@angular/router';
import { UsuarioModel } from './../../models/user-model';
import { ToastrService } from 'ngx-toastr';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { SessionService } from './session.service';
import { CurrentUserService } from '../user/current-user.service';
import { IdentityApiService } from '../user/identity-api.service';
import { AuthResponseModel } from '../../models/auth-response-model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private tempoLogin = 25200000;

  constructor(
    private toastrService: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private sessionService: SessionService,
    private currentUserService: CurrentUserService,
    private identityApiService: IdentityApiService,
  ) { }

  login(email: string, senha: string): Observable<AuthResponseModel> {
    return this.identityApiService.login(email, senha).pipe(
      tap(response => {
        if (response && response.token) {
          const now = new Date().getTime();
          this.sessionService.setAuthToken(response.token, now + this.tempoLogin);
          this.sessionService.setEhProdutor(!!response.ehProdutor);
        }
      }),
      switchMap((response) => this.identityApiService.getDadosUsuario().pipe(
        tap((user) => this.currentUserService.setCurrentUser(user)),
        map(() => response),
        catchError(() => {
          this.currentUserService.clearCurrentUser();
          return of(response);
        })
      ))
    );
  }

  criarConta(user: UsuarioModel): Observable<AuthResponseModel> {
    return this.identityApiService.cadastrar(user).pipe(
      tap(response => {
        if (response && response.token) {
          const now = new Date().getTime();
          this.sessionService.setAuthToken(response.token, now + this.tempoLogin);
        }
      }),
      switchMap((response) => this.identityApiService.getDadosUsuario().pipe(
        tap((loadedUser) => this.currentUserService.setCurrentUser(loadedUser)),
        map(() => response),
        catchError(() => {
          this.currentUserService.clearCurrentUser();
          return of(response);
        })
      ))
    );
  }

  redefinirSenha(emailOrCpf: String) {
    return this.identityApiService.redefinirSenha(emailOrCpf);
  }

  confRedefinirSenha(code: string, novaSenha: string) {
    return this.identityApiService.gerarNovaSenha(code, novaSenha);
  }

  logout(tokenVencido: boolean = false) {
    this.sessionService.clearAuthToken();
    this.sessionService.clearEhProdutor();
    this.currentUserService.clearCurrentUser();

    if (!tokenVencido) {
      this.toastrService.info('Usuário deslogado');
      this.redirecionarParaLogin();
    } else {
      this.toastrService.warning('Sessão expirada. Faça login novamente.');
    }
  }

  isLoggedIn(): boolean {
    return !!this.sessionService.getAuthToken();
  }

  recuperarToken(): string {
    return this.sessionService.getAuthToken();
  }

  deslogarClienteTokenInvalido() {
    this.logout(true);
    this.redirecionarParaLogin();
  }

  redirecionarParaLogin() {
    this.router.navigate(['/eventos/entrar-conta']);
  }

  salvarUltimaUrl(pagina: string) {
    if (pagina != '/eventos/entrar-conta') {
      this.sessionService.setUltimaUrl(pagina);
    }
  }

  recuperarUltimaUrl(): string {
    return this.sessionService.getUltimaUrl() || '/eventos';
  }
}
